import { FC, FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import Markdown from "markdown-to-jsx";
import Card from "components/card/Card";
import {
  useAllReleaseNotes,
  useReleaseNoteAdmin,
  Localized,
  ReleaseNote,
  ReleaseNoteLang,
  ReleaseNoteTag,
} from "lib/releaseNoteQueries";
import "./ReleaseNotesAdmin.scss";

const LANGS: ReleaseNoteLang[] = ["nl", "en", "de"];
const TAGS: ReleaseNoteTag[] = ["new", "improved", "fixed"];

/** Every language present, so the form can index it without undefined checks. */
type LangText = Record<ReleaseNoteLang, string>;

const emptyText = (): LangText => ({ nl: "", en: "", de: "" });

const toLangText = (field: Localized | undefined): LangText => ({
  nl: field?.nl ?? "",
  en: field?.en ?? "",
  de: field?.de ?? "",
});

/** Drop blank languages, so an untouched language never lands in the payload. */
const toLocalized = (text: LangText): Localized => {
  const out: Localized = {};
  LANGS.forEach((lang) => {
    const value = text[lang].trim();
    if (value) out[lang] = value;
  });
  return out;
};

interface FormState {
  /** null while creating; the note's id while editing. */
  editingId: string | null;
  version: string;
  date: string;
  tag: "" | ReleaseNoteTag;
  published: boolean;
  title: LangText;
  summary: LangText;
  details: LangText;
}

const blankForm = (): FormState => ({
  editingId: null,
  version: "",
  date: "",
  tag: "",
  published: false,
  title: emptyText(),
  summary: emptyText(),
  details: emptyText(),
});

const formFor = (note: ReleaseNote): FormState => ({
  editingId: note.id,
  version: note.version,
  date: note.date ?? "",
  tag: note.tag ?? "",
  published: note.published,
  title: toLangText(note.title),
  summary: toLangText(note.summary),
  details: toLangText(note.details),
});

const ReleaseNotesAdmin: FC = () => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useAllReleaseNotes();
  const { create, update, remove } = useReleaseNoteAdmin();

  const [form, setForm] = useState<FormState>(blankForm);
  const [lang, setLang] = useState<ReleaseNoteLang>("nl");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const notes = data ?? [];
  const isEditing = form.editingId !== null;
  const canSave = form.version.trim().length > 0 && !saving;

  const setText = (
    field: "title" | "summary" | "details",
    value: string
  ): void =>
    setForm((prev) => ({
      ...prev,
      [field]: { ...prev[field], [lang]: value },
    }));

  const resetForm = (): void => {
    setForm(blankForm());
    setLang("nl");
    setError(null);
  };

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!canSave) return;

    const payload = {
      version: form.version.trim(),
      date: form.date ? form.date : null,
      tag: form.tag ? form.tag : null,
      published: form.published,
      title: toLocalized(form.title),
      summary: toLocalized(form.summary),
      details: toLocalized(form.details),
    };

    setSaving(true);
    setError(null);
    try {
      if (form.editingId) {
        await update(form.editingId, payload);
      } else {
        await create(payload);
      }
      resetForm();
    } catch {
      setError(t("releaseNotesAdmin.saveError"));
    } finally {
      setSaving(false);
    }
  };

  // Publishing is the most common action, so it gets a control on the row
  // itself rather than a trip through the form.
  const onTogglePublished = async (note: ReleaseNote): Promise<void> => {
    setBusyId(note.id);
    setError(null);
    try {
      await update(note.id, { published: !note.published });
    } catch {
      setError(t("releaseNotesAdmin.saveError"));
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (note: ReleaseNote): Promise<void> => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t("releaseNotesAdmin.confirmDelete", { version: note.version })))
      return;
    setBusyId(note.id);
    setError(null);
    try {
      await remove(note.id);
      if (form.editingId === note.id) resetForm();
    } catch {
      setError(t("releaseNotesAdmin.deleteError"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="rn-admin">
      <h1 className="rn-admin__heading">{t("releaseNotesAdmin.title")}</h1>
      <p className="rn-admin__intro">{t("releaseNotesAdmin.intro")}</p>

      <Card customClass="rn-admin__form-card">
        <form onSubmit={onSubmit} className="rn-admin__form">
          <h2 className="rn-admin__form-heading">
            {isEditing
              ? t("releaseNotesAdmin.editHeading")
              : t("releaseNotesAdmin.newHeading")}
          </h2>

          <div className="rn-admin__meta">
            <label className="rn-admin__field" htmlFor="rn-version">
              <span>{t("releaseNotesAdmin.version")}</span>
              <input
                id="rn-version"
                type="text"
                value={form.version}
                placeholder="02.05.00"
                maxLength={50}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, version: e.target.value }))
                }
                data-testid="rn-version-input"
              />
            </label>

            <label className="rn-admin__field" htmlFor="rn-date">
              <span>{t("releaseNotesAdmin.date")}</span>
              <input
                id="rn-date"
                type="date"
                value={form.date}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, date: e.target.value }))
                }
                data-testid="rn-date-input"
              />
            </label>

            <label className="rn-admin__field" htmlFor="rn-tag">
              <span>{t("releaseNotesAdmin.tag")}</span>
              <select
                id="rn-tag"
                value={form.tag}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    tag: e.target.value as "" | ReleaseNoteTag,
                  }))
                }
                data-testid="rn-tag-select"
              >
                <option value="">{t("releaseNotesAdmin.tagNone")}</option>
                {TAGS.map((tag) => (
                  <option key={tag} value={tag}>
                    {t(`releaseNotesAdmin.tag.${tag}`)}
                  </option>
                ))}
              </select>
            </label>

            <label className="rn-admin__checkbox" htmlFor="rn-published">
              <input
                id="rn-published"
                type="checkbox"
                checked={form.published}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, published: e.target.checked }))
                }
                data-testid="rn-published-checkbox"
              />
              <span>{t("releaseNotesAdmin.published")}</span>
            </label>
          </div>

          <div className="rn-admin__langs" role="tablist">
            {LANGS.map((code) => (
              <button
                key={code}
                type="button"
                role="tab"
                aria-selected={lang === code}
                className={`rn-admin__lang ${
                  lang === code ? "rn-admin__lang--active" : ""
                }`}
                onClick={() => setLang(code)}
                data-testid={`rn-lang-${code}`}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>

          <label className="rn-admin__field" htmlFor="rn-title">
            <span>{t("releaseNotesAdmin.noteTitle")}</span>
            <input
              id="rn-title"
              type="text"
              value={form.title[lang]}
              maxLength={200}
              onChange={(e) => setText("title", e.target.value)}
              data-testid="rn-title-input"
            />
          </label>

          <label className="rn-admin__field" htmlFor="rn-summary">
            <span>{t("releaseNotesAdmin.summary")}</span>
            <textarea
              id="rn-summary"
              rows={2}
              value={form.summary[lang]}
              maxLength={500}
              onChange={(e) => setText("summary", e.target.value)}
              data-testid="rn-summary-input"
            />
          </label>

          <div className="rn-admin__details">
            <label className="rn-admin__field" htmlFor="rn-details">
              <span>{t("releaseNotesAdmin.details")}</span>
              <textarea
                id="rn-details"
                rows={12}
                value={form.details[lang]}
                onChange={(e) => setText("details", e.target.value)}
                data-testid="rn-details-input"
              />
            </label>

            <div className="rn-admin__preview">
              <span className="rn-admin__preview-label">
                {t("releaseNotesAdmin.preview")}
              </span>
              <div
                className="rn-admin__preview-body release-note__details"
                data-testid="rn-details-preview"
              >
                {form.details[lang] ? (
                  <Markdown>{form.details[lang]}</Markdown>
                ) : (
                  <span className="rn-admin__preview-empty">
                    {t("releaseNotesAdmin.previewEmpty")}
                  </span>
                )}
              </div>
            </div>
          </div>

          {error && <div className="rn-admin__error">{error}</div>}

          <div className="rn-admin__actions">
            <button
              type="submit"
              className="rn-admin__submit"
              disabled={!canSave}
              data-testid="rn-save"
            >
              {t(isEditing ? "releaseNotesAdmin.save" : "releaseNotesAdmin.add")}
            </button>
            {isEditing && (
              <button
                type="button"
                className="rn-admin__cancel"
                onClick={resetForm}
                data-testid="rn-cancel"
              >
                {t("releaseNotesAdmin.cancel")}
              </button>
            )}
          </div>
        </form>
      </Card>

      <h2 className="rn-admin__section-label">
        {t("releaseNotesAdmin.existing")}
      </h2>

      {isLoading && (
        <div className="rn-admin__state">{t("common.loading")}</div>
      )}
      {isError && (
        <div className="rn-admin__state">{t("releaseNotesAdmin.loadError")}</div>
      )}
      {!isLoading && !isError && notes.length === 0 && (
        <div className="rn-admin__state">{t("releaseNotesAdmin.empty")}</div>
      )}

      {notes.length > 0 && (
        <ul className="rn-admin__list">
          {notes.map((note) => (
            <li key={note.id} className="rn-admin__row" data-testid="release-note-row">
              <span className="rn-admin__row-ver">v{note.version}</span>
              <span
                className={`rn-admin__badge rn-admin__badge--${
                  note.published ? "published" : "draft"
                }`}
              >
                {t(
                  note.published
                    ? "releaseNotesAdmin.publishedBadge"
                    : "releaseNotesAdmin.draft"
                )}
              </span>
              <span className="rn-admin__row-summary">
                {note.summary.nl ?? note.summary.en ?? note.summary.de ?? ""}
              </span>
              <span className="rn-admin__row-date">{note.date ?? "—"}</span>

              <div className="rn-admin__row-actions">
                <button
                  type="button"
                  onClick={() => onTogglePublished(note)}
                  disabled={busyId === note.id}
                  data-testid={`rn-publish-${note.id}`}
                >
                  {t(
                    note.published
                      ? "releaseNotesAdmin.unpublish"
                      : "releaseNotesAdmin.publish"
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setForm(formFor(note))}
                  data-testid={`rn-edit-${note.id}`}
                >
                  {t("releaseNotesAdmin.edit")}
                </button>
                <button
                  type="button"
                  className="rn-admin__delete"
                  onClick={() => onDelete(note)}
                  disabled={busyId === note.id}
                  data-testid={`rn-delete-${note.id}`}
                >
                  {t("releaseNotesAdmin.delete")}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ReleaseNotesAdmin;
